/**
 * Ad-posting forms for the web app: the schema built from the field workbooks
 * (tools/forms/build_schema.py) plus the same rules the API applies (apps/api/app/forms/schema.py).
 * parity_cases.json is checked by both test suites so the two implementations cannot drift apart.
 */
import schemaJson from "./schema.json";

export type Rule = "required" | "optional" | "conditional" | "system";
export type ValueType =
  | "text" | "text_long" | "int" | "decimal" | "money" | "bool" | "enum" | "enum_list" | "measure"
  | "location" | "phone" | "email" | "url" | "pincode" | "geo" | "date" | "month" | "year" | "time"
  | "datetime" | "media" | "object";

export interface Condition {
  field?: string;
  in?: string[];
  equals?: unknown;
  gt?: number;
  any?: Condition[];
}

export interface Field {
  name: string;
  label: string;
  rule: Rule;
  type: ValueType;
  control: string;
  options?: string[];
  strict?: boolean;
  hint?: string;
  notes?: string;
  group?: string;
  when?: Condition;
  required_when_shown?: boolean;
  /** uploads: the one kind of file taken (others are refused) */
  accept?: "image" | "video" | "document";
  /** uploads: most files (e.g. 1 for a floor plan image or the brochure) */
  max?: number;
}

export interface Section {
  key: string;
  title: string;
  fields: Field[];
}

export interface Group {
  title: string;
  min: number;
  max: number;
  fields: Field[];
}

export interface Form {
  key: string;
  module: "property" | "hall" | "service";
  title: string;
  purpose?: "sell" | "rent" | "pg";
  category?: string;
  property_types?: string[];
  subtypes?: string[];
  /** A new project (builders and verified agents): unit types, floor plans per unit type, nearby places. */
  project?: boolean;
  sections: Section[];
  groups: Record<string, Group>;
}

export type Answers = Record<string, unknown>;

export const forms: Record<string, Form> = (schemaJson as unknown as { forms: Record<string, Form> }).forms;

export function getForm(key: string): Form | undefined {
  return forms[key];
}

/** "1 ... total floors" in a list of choices: the floors 1 to the building's total (the "total_floors" answer). */
const UP_TO_TOTAL_FLOORS = /^(\d+)\s*\.\.\.\s*total floors$/i;
export const MAX_FLOORS = 300;

/** A person's name on an ad (the API has the same rule): letters of any script and single spaces. */
export const NAME_FIELDS = ["contact_name", "contact_person"];
export const NAME_MESSAGE = "Please enter a valid name using alphabets only.";
export function nameProblem(value: string): string | null {
  const name = value.trim().replace(/\s+/g, " ");
  return !name || /^\p{L}[\p{L}\p{M}]*(?: \p{L}[\p{L}\p{M}]*)*$/u.test(name) ? null : NAME_MESSAGE;
}

/** The choices to offer for a field: "1 ... total floors" becomes 1, 2 ... up to the floors entered in
 * "Total floors in building" (nothing until it is entered). Other fields keep their options. */
export function choicesFor(field: Field, answers: Answers): string[] {
  const out: string[] = [];
  for (const o of field.options ?? []) {
    const m = UP_TO_TOTAL_FLOORS.exec(o);
    if (!m) {
      out.push(o);
      continue;
    }
    const total = Number(answers.total_floors);
    if (Number.isInteger(total) && total >= Number(m[1])) {
      for (let n = Number(m[1]); n <= Math.min(total, MAX_FLOORS); n++) out.push(String(n));
    }
  }
  return out;
}

export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value as object).length === 0;
  return false;
}

/** Same semantics as condition_holds() in the API: in / equals / gt / any; no condition = always. */
export function conditionHolds(cond: Condition | null | undefined, answers: Answers): boolean {
  if (!cond) return true;
  if (cond.any) return cond.any.some((c) => conditionHolds(c, answers));
  const value = cond.field ? answers[cond.field] : undefined;
  if (cond.in) return typeof value === "string" && cond.in.includes(value);
  if ("equals" in cond) return value === cond.equals;
  if (cond.gt !== undefined) {
    if (value === null || value === undefined || typeof value === "boolean" || value === "") return false;
    const n = Number(value);
    return Number.isFinite(n) && n > cond.gt;
  }
  return false;
}

/** Shown to the poster: not set by RealRoof and not hidden by an earlier answer. */
export function isShown(field: Field, answers: Answers): boolean {
  return field.rule !== "system" && conditionHolds(field.when, answers);
}

/** Must be filled before submitting. */
export function isNeeded(field: Field, answers: Answers): boolean {
  return field.rule === "required" || (Boolean(field.required_when_shown) && conditionHolds(field.when, answers));
}

/** The fields still to fill, as the API reports them (`spaces[0].name` for group items). */
export function missingRequired(form: Form, answers: Answers): string[] {
  const missing: string[] = [];
  const groups = form.groups ?? {};
  for (const section of form.sections) {
    for (const f of section.fields) {
      if (!(f.name in groups) && isNeeded(f, answers) && isEmpty(answers[f.name])) missing.push(f.name);
    }
  }
  for (const [name, group] of Object.entries(groups)) {
    const items = Array.isArray(answers[name]) ? (answers[name] as Answers[]) : [];
    if (items.length < group.min) missing.push(name);
    items.slice(0, group.max).forEach((item, i) => {
      const context = { ...answers, ...item };
      for (const f of group.fields) {
        if (isNeeded(f, context) && isEmpty(item[f.name])) missing.push(`${name}[${i}].${f.name}`);
      }
    });
  }
  return missing.sort();
}

export interface Step {
  key: string;
  title: string;
  fields: Field[];
  /** set for the repeatable hall / menu cards */
  group?: string;
}

/** Where repeatable groups go: after the section whose title starts with this. */
const GROUP_AFTER: Record<string, string> = {
  spaces: "Capacity",
  packages: "Pricing",
  floor_plans: "Description",
  unit_types: "Project", // new projects: Project, Unit types, Amenities, Nearby places ...
  nearby: "Amenities",
  progress: "Media", // a project's monthly site photos, after its photos and brochure
};
/** A section with this many visible fields or fewer joins the step before it. */
const SMALL_SECTION = 2;

/** One step per workbook section; tiny sections merge into the previous step; system fields never show. */
export function steps(form: Form): Step[] {
  const out: Step[] = [];
  const placed = new Set<string>();
  for (const section of form.sections) {
    const fields = section.fields.filter((f) => f.rule !== "system");
    if (fields.length > 0) {
      const previous = out.at(-1);
      // a service's "Details & Pricing" is always its own step, whatever its size
      if (fields.length <= SMALL_SECTION && previous && !previous.group && section.title !== "Details & Pricing") {
        previous.fields.push(...fields);
        previous.title = `${previous.title} & ${section.title}`;
      } else {
        out.push({ key: section.key, title: section.title, fields: [...fields] });
      }
    }
    for (const [group, after] of Object.entries(GROUP_AFTER)) {
      if (form.groups?.[group] && !placed.has(group) && section.title.startsWith(after)) {
        out.push({ key: group, title: form.groups[group].title, fields: form.groups[group].fields, group });
        placed.add(group);
      }
    }
  }
  for (const [group, def] of Object.entries(form.groups ?? {})) {
    if (!placed.has(group)) out.push({ key: group, title: def.title, fields: def.fields, group });
  }
  return out;
}

/** The step a field (or "spaces[0].name") belongs to, for "fix this" links on the review step. */
export function stepOf(form: Form, path: string): number {
  const name = path.split(/[[.]/)[0];
  return steps(form).findIndex((s) => s.group === name || (!s.group && s.fields.some((f) => f.name === name)));
}

// ---------- how each field is entered ----------

/** The editor for a compound ("object") answer, chosen from the workbook's control text. Stored shapes:
 * counts {option: n} · choice_amount {choice, amount?, negotiable?} · pair {[key]: n, unit?} ·
 * time_range {start, end, all_day?} · per_type_number {type: n} · per_type_list {type: [options]} ·
 * chip_times {option: {start, end}} · text_number {name, km} · toggles {[key]: bool} ·
 * price_rules [{when, price}] · choice_text {choice, details?} · place_text {locality_id?, address?} ·
 * chips_other {chosen: [...], other?} · range_on_request {min?, max?, on_request?} · year {choice, year?} ·
 * rooms {unit: "ft" | "m", rooms: [{name, length, width}]} (floor plan room sizes) */
export type ObjectEditor =
  | "counts" | "choice_amount" | "pair" | "time_range" | "per_type_number" | "per_type_list" | "chip_times"
  | "text_number" | "toggles" | "price_rules" | "choice_text" | "place_text" | "chips_other"
  | "range_on_request" | "year" | "rooms";

const OBJECT_EDITORS: [RegExp, ObjectEditor][] = [
  [/counters|chips \(multi\) \+ number|count dropdown per/i, "counts"],
  [/^radio( \(.*\))? \+ number/i, "choice_amount"],
  [/^two number (boxes|steppers)/i, "pair"],
  [/^two time pickers/i, "time_range"],
  [/^number (box|stepper) per type/i, "per_type_number"],
  [/^checkboxes per type/i, "per_type_list"],
  [/^chips \+ (time pickers|start\/end time)/i, "chip_times"],
  [/^text box \+ number/i, "text_number"],
  [/^two toggles/i, "toggles"],
  [/^table: /i, "price_rules"],
  [/^(dropdown|radio) \+ text area/i, "choice_text"],
  [/^autocomplete \+ text/i, "place_text"],
  [/^chips \+ other/i, "chips_other"],
  [/'on request' toggle/i, "range_on_request"],
  [/^year picker/i, "year"],
  [/^room sizes table/i, "rooms"],
];

export function objectEditor(field: Field): ObjectEditor | undefined {
  return OBJECT_EDITORS.find(([re]) => re.test(field.control))?.[1];
}

const PAIR_KEYS: Record<string, [string, string]> = {
  plot_dimensions: ["length", "breadth"],
  lifts: ["passenger", "service"],
};

/** The two numbers of a "pair" answer, e.g. ["min", "max"] or ["length", "breadth"]. */
export function pairKeys(field: Field): [string, string] {
  return PAIR_KEYS[field.name] ?? ["min", "max"];
}

/** The two switches of a "toggles" answer, from the label: "Fireworks / baraat allowed" -> fireworks, baraat. */
export function toggleKeys(field: Field): string[] {
  return field.label.replace(/ allowed$/i, "").split("/").map((s) => s.trim().toLowerCase()).filter(Boolean);
}

/** The answer that lists the types for "per type" fields (PG rooms). */
export const PER_TYPE_SOURCE = "room_types";

export type InputKind =
  | "text" | "textarea" | "number" | "money" | "chips" | "select" | "multi" | "yesno" | "measure"
  | "city" | "locality" | "place" | "geo" | "media" | "phone" | "email" | "url" | "pincode"
  | "date" | "month" | "time" | "year" | "datetime" | "object";

/** Chips while the options fit on a line or two; a dropdown beyond that. */
const CHIP_LIMIT = 8;

export function inputKind(field: Field): InputKind {
  switch (field.type) {
    case "text": return "text";
    case "text_long": return "textarea";
    case "int": case "decimal": return "number";
    case "money": return "money";
    case "bool": return "yesno";
    case "enum":
      if (!field.options?.length) return "text";
      return field.options.length <= CHIP_LIMIT && !/dropdown/i.test(field.control) ? "chips" : "select";
    case "enum_list": return "multi";
    case "measure": return "measure";
    case "location":
      if (field.name === "city_id") return "city";
      return field.name === "locality_id" ? "locality" : "place";
    case "object": return "object";
    default: return field.type;
  }
}

/** Most choices allowed in a multi-select ("Chips (multi, max 5)"). */
export function maxChoices(field: Field): number | undefined {
  const m = /max\s*(\d+)/i.exec(field.control);
  return m ? Number(m[1]) : undefined;
}

/** Media fields that also take links (YouTube, albums, "Text boxes"). */
export function takesLinks(field: Field): boolean {
  return /link|text boxes/i.test(field.control) || field.name === "links";
}

export { answerSections, formatAnswer, plain, roomsOf, roomSize, rupees, shortRupees, type AnswerRow, type AnswerSection, type Room } from "./format";
